import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Tags, Copy } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import AlertDialog from '../../../components/ui/AlertDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription,
} from '../../../components/ui/Dialog';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import {
  getCustomFieldDefinitions, createCustomFieldDefinition, updateCustomFieldDefinition,
  deleteCustomFieldDefinition, CustomFieldDefinitionView,
} from '../../../api/General/customFields';

interface EditState { id?: number; label: string; labelAr: string; }

// Real CRUD for HR-defined custom employee fields (text only). Each field becomes a
// {{custom_<key>}} placeholder in the Document Template Manager and an editable row on the
// employee profile.
export default function CustomFieldsPanel() {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [fields, setFields] = useState<CustomFieldDefinitionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [edit, setEdit] = useState<EditState>({ label: '', labelAr: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldDefinitionView | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAr = i18n.language?.startsWith('ar');
  const label = (f: CustomFieldDefinitionView) => (isAr && f.labelAr ? f.labelAr : f.label);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCustomFieldDefinitions();
      if (!res.errors?.length) setFields(res.payload ?? []);
      else showToast(res.errors[0]?.message || t('Failed to load custom fields'), 'error');
    } catch {
      showToast(t('Failed to load custom fields'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEdit({ label: '', labelAr: '' }); setDialogOpen(true); };
  const openEdit = (f: CustomFieldDefinitionView) => { setEdit({ id: f.id, label: f.label, labelAr: f.labelAr ?? '' }); setDialogOpen(true); };

  const save = async () => {
    if (!edit.label.trim()) { showToast(t('Field label is required'), 'error'); return; }
    setSaving(true);
    try {
      const payload = { id: edit.id, label: edit.label.trim(), labelAr: edit.labelAr.trim() || undefined, displayOrder: edit.id ? (fields.find(f => f.id === edit.id)?.displayOrder ?? 0) : fields.length };
      const res = edit.id ? await updateCustomFieldDefinition(payload) : await createCustomFieldDefinition(payload);
      if (res.errors?.length) { showToast(res.errors[0]?.message || t('Failed to save custom field'), 'error'); return; }
      showToast(edit.id ? t('Custom field updated') : t('Custom field added'), 'success');
      setDialogOpen(false);
      await load();
    } catch {
      showToast(t('Failed to save custom field'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteCustomFieldDefinition(deleteTarget.id);
      if (res.errors?.length) { showToast(res.errors[0]?.message || t('Failed to delete custom field'), 'error'); return; }
      showToast(t('Custom field deleted'), 'success');
      await load();
    } catch {
      showToast(t('Failed to delete custom field'), 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const copyToken = (key: string) => {
    const token = `{{custom_${key}}}`;
    navigator.clipboard?.writeText(token).then(
      () => showToast(t('Copied {{token}}', { token }), 'success', 2500),
      () => { /* clipboard blocked; ignore */ }
    );
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Tags size={22} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('Custom Fields')}</h3>
            <p className="text-sm text-muted-foreground">{t('Define extra employee fields. Each one can be inserted into document templates.')}</p>
          </div>
        </div>
        <Button onClick={openAdd} iconName="Plus">{t('Add field')}</Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('Loading...')}</div>
      ) : fields.length === 0 ? (
        <div className="py-12 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <Plus size={22} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('No custom fields yet')}</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">{t('Add a field like Employee Grade or Cost Center, then fill it per employee and use it in templates.')}</p>
          <Button onClick={openAdd} iconName="Plus" variant="outline" className="mt-4">{t('Add field')}</Button>
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {fields.map(f => (
            <li key={f.id} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{label(f)}</p>
                {f.labelAr && !isAr && <p className="text-xs text-muted-foreground truncate" dir="rtl">{f.labelAr}</p>}
                <button
                  type="button"
                  onClick={() => copyToken(f.key)}
                  className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
                  title={t('Copy placeholder')}
                >
                  {`{{custom_${f.key}}}`}
                  <Copy size={12} />
                </button>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" iconName="Pencil" onClick={() => openEdit(f)} aria-label={t('Edit')} />
                <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => setDeleteTarget(f)} aria-label={t('Delete')} className="text-destructive hover:text-destructive" />
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={o => { if (!saving) setDialogOpen(o); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{edit.id ? t('Edit custom field') : t('Add custom field')}</DialogTitle>
            <DialogDescription>{t('Text fields shown on the employee profile and available as template placeholders.')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <Input
              label={t('Field label')}
              required
              placeholder={t('e.g. Employee Grade')}
              value={edit.label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdit({ ...edit, label: e.target.value })}
            />
            <Input
              label={t('Arabic label (optional)')}
              placeholder={t('e.g. درجة الموظف')}
              dir="rtl"
              value={edit.labelAr}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdit({ ...edit, labelAr: e.target.value })}
            />
            {!edit.id && (
              <p className="text-xs text-muted-foreground">{t('A template placeholder is generated automatically from the label.')}</p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t('Cancel')}</Button>
            <Button onClick={save} loading={saving} disabled={saving}>{edit.id ? t('Save changes') : t('Add field')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={o => { if (!o) setDeleteTarget(null); }}
        title={t('Delete custom field')}
        description={t('Remove "{{name}}"? Existing values are cleared and any template using its placeholder will leave it blank.', { name: deleteTarget ? label(deleteTarget) : '' })}
        variant="destructive"
        confirmLabel={t('Delete field')}
        cancelLabel={t('Cancel')}
        isLoading={deleting}
        onConfirm={confirmDelete}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
